import { NavLink, Link, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Stack, List, X } from "@phosphor-icons/react";

const TABS = [
  ["Home", "/"],
  ["Who We Serve", "/who-we-serve"],
  ["Our Approach", "/our-approach"],
  ["How Engagement Works", "/how-engagement-works"],
  ["Outcomes", "/outcomes"],
  ["Trust & Validation", "/trust"],
];

export const SiteLayout = ({ children }) => {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); setOpen(false); }, [pathname]);
  return (
    <div data-testid="public-site" className="bg-slate-950 text-left min-h-screen">
      <nav className="sticky top-0 z-30 bg-slate-950/85 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex items-center gap-6">
          <Link to="/" data-testid="site-logo" className="flex items-center gap-2.5 shrink-0">
            <Stack size={26} weight="fill" className="text-emerald-400" />
            <span className="font-heading font-extrabold text-white tracking-tight text-lg">MAI MATRIX<span className="align-super text-[9px]">™</span></span>
          </Link>
          <div className="hidden lg:flex items-center gap-1 flex-1">
            {TABS.map(([label, to]) => (
              <NavLink key={to} to={to} end={to === "/"}
                data-testid={`site-nav-${to === "/" ? "home" : to.slice(1)}`}
                className={({ isActive }) =>
                  `text-xs font-semibold px-3 py-2 ${isActive ? "text-white border-b-2 border-emerald-500" : "text-slate-400 hover:text-white"}`}>
                {label}
              </NavLink>
            ))}
          </div>
          <Link to="/request-demo" data-testid="site-nav-demo"
            className="ml-auto hidden lg:inline-block bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold px-4 py-2.5">Request a Demo</Link>
          <button data-testid="site-nav-mobile-toggle" onClick={() => setOpen(!open)} className="lg:hidden ml-auto text-white p-1">
            {open ? <X size={22} /> : <List size={22} />}
          </button>
        </div>
        {open && (
          <div data-testid="site-nav-mobile" className="lg:hidden border-t border-white/10 px-6 py-4 space-y-1 bg-slate-950">
            {[...TABS, ["Request a Demo", "/request-demo"]].map(([label, to]) => (
              <NavLink key={to} to={to} end={to === "/"}
                className={({ isActive }) => `block text-sm font-semibold py-2 ${isActive ? "text-emerald-400" : "text-slate-300"}`}>
                {label}
              </NavLink>
            ))}
          </div>
        )}
      </nav>
      {children}
      <footer className="bg-slate-950 border-t border-white/10 py-10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <div className="flex items-center gap-2">
              <Stack size={20} weight="fill" className="text-emerald-400" />
              <span className="font-heading font-bold text-white text-base tracking-tight">MAI MATRIX™</span>
            </div>
            <p className="text-[11px] text-slate-500 mt-2 max-w-md leading-relaxed">
              A product of MarksMan Healthcare — HEOR, real-world evidence and market access consulting for pharmaceutical and MedTech innovators worldwide.
            </p>
          </div>
          <div className="text-[11px] text-slate-600 md:text-right">
            <Link to="/request-demo" className="text-slate-400 hover:text-white font-semibold">Request a Demo →</Link>
            <p className="mt-2">© {new Date().getFullYear()} MarksMan Healthcare. All rights reserved.<br />GMARI™ and MAI Matrix™ are trademarks.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export const PageHero = ({ overline, title, lede }) => (
  <header className="relative overflow-hidden border-b border-white/10">
    <div className="absolute inset-0" style={{ backgroundImage: "linear-gradient(rgba(148,163,184,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(148,163,184,0.06) 1px, transparent 1px)", backgroundSize: "48px 48px" }} />
    <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-16">
      <p className="overline-label text-emerald-400">{overline}</p>
      <h1 className="font-heading font-extrabold text-white text-3xl sm:text-4xl lg:text-5xl tracking-tight mt-3 max-w-3xl">{title}</h1>
      {lede && <p className="text-slate-400 text-base leading-relaxed mt-5 max-w-2xl">{lede}</p>}
    </div>
  </header>
);

export const LightSection = ({ overline, title, children, alt = false }) => (
  <section className={`${alt ? "bg-white" : "bg-[#F8FAFC]"} py-16 border-b border-slate-200`}>
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      {overline && <p className="overline-label text-slate-400">{overline}</p>}
      {title && <h2 className="font-heading font-extrabold text-slate-900 text-2xl md:text-3xl tracking-tight mt-2 max-w-2xl">{title}</h2>}
      <div className={overline || title ? "mt-10" : ""}>{children}</div>
    </div>
  </section>
);

export const CtaStrip = ({ text = "Wondering whether it's the right time? A 30-minute conversation will tell you." }) => (
  <section className="bg-slate-950 py-14">
    <div className="max-w-7xl mx-auto px-6 lg:px-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
      <p className="font-heading font-bold text-white text-xl md:text-2xl tracking-tight max-w-xl">{text}</p>
      <Link to="/request-demo" data-testid="cta-strip-demo"
        className="bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-semibold px-6 py-3.5 shrink-0 inline-block">Request a Demo</Link>
    </div>
  </section>
);
